import {
  Dices,
  MessageSquare,
  PanelsTopLeft,
  ScrollText,
  SendHorizontal,
  SlidersHorizontal,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { parseDiceExpression } from "../lib/dice";
import {
  COMMON_CONDITIONS,
  ConditionBadges,
  parseConditions,
  setConditionsValue,
} from "../scene/condition-badges";
import { HpBar } from "../scene/hp-bar";
import { appendBlock, createEdge, updateBlock } from "../state/graph-actions";
import {
  type BlockSummary,
  useBlock,
  useBlockSummaries,
} from "../state/graph-hooks";
import type { BlockNode } from "../state/graph-types";
import { useInteractionStore } from "../state/interaction-store";
import {
  type ChatEventEntryKind,
  type ChatEventMetadataDiceRoll,
  type DiceRollVisibility,
  type PropertyDef,
  useTypeDef,
  useTypeMap,
} from "../state/type-registry";
import { useUIStore } from "../state/ui-store";
import { useYDoc } from "../state/yjs-provider";
import { DiceRoller } from "./dice-roller";
import { IconButton } from "./icon-button";
import { LancerPanel } from "./lancer-panel";

const DOC_ROOT_ID = "__document_root__";
const SCENE_TIMELINE_EDGE_KIND = "scene-timeline";

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export function RightPanel() {
  const { isRightPanelOpen, activeRightTab, setRightTab, lancerPoppedOut } =
    useUIStore();
  const selection = useInteractionStore((s) => s.selection);
  const setSelection = useInteractionStore((s) => s.setSelection);
  const doc = useYDoc();
  const blockSummaries = useBlockSummaries();
  const typeMap = useTypeMap();
  const [panelWidth, setPanelWidth] = useState(280);
  const diceVisibility: DiceRollVisibility = "gm-only";
  const effectiveWidth =
    activeRightTab === "lancer" && lancerPoppedOut
      ? Math.max(panelWidth, 520)
      : panelWidth;
  const selectedId = selection.length > 0 ? selection[0] : null;
  const sceneAnchorId = useMemo(
    () => resolveSceneAnchorId(blockSummaries),
    [blockSummaries],
  );

  if (!isRightPanelOpen) return null;

  const tabs = [
    { id: "inspector", label: "Details", icon: SlidersHorizontal },
    { id: "chat", label: "Chat", icon: MessageSquare },
    { id: "lancer", label: "Lancer", icon: PanelsTopLeft },
  ] as const;
  type TabId = (typeof tabs)[number]["id"];

  const startResize = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const initialWidth = panelWidth;

    const onMove = (moveEvent: MouseEvent) => {
      const delta = startX - moveEvent.clientX;
      const next = Math.min(520, Math.max(220, initialWidth + delta));
      setPanelWidth(next);
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const renderTabContent = (tabId: TabId) => {
    if (tabId === "lancer") {
      return <LancerPanel currentVisibility={diceVisibility} />;
    }

    if (tabId === "inspector") {
      return selectedId ? (
        <BlockInspector blockId={selectedId} />
      ) : (
        <div className="text-white/30 text-[10px] mt-2">
          Select a node to inspect.
        </div>
      );
    }

    return (
      <ChatFeed
        doc={doc}
        blocks={blockSummaries}
        typeMap={typeMap}
        selectedEntityId={selectedId}
        sceneAnchorId={sceneAnchorId}
        onSelectEntity={(id) => setSelection([id])}
      />
    );
  };

  return (
    <div
      className="h-full pointer-events-auto flex flex-col bg-black/40 backdrop-blur-sm border border-white/10 rounded text-white/70 shadow-2xl overflow-hidden relative"
      style={{ width: `${effectiveWidth}px` }}
    >
      <div
        className="absolute left-0 top-0 h-full w-1 cursor-ew-resize hover:bg-white/10"
        onMouseDown={startResize}
        title="Resize panel"
      />

      {/* Top icon toggle group */}
      <div className="border-b border-white/5 p-[2px] flex items-center gap-px">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <IconButton
              key={tab.id}
              tooltip={tab.label}
              className={`w-6 h-5 rounded flex items-center justify-center transition-colors ${
                activeRightTab === tab.id
                  ? "text-white/90 bg-white/12"
                  : "text-white/40 hover:text-white/70 hover:bg-white/8"
              }`}
              onClick={() => setRightTab(tab.id)}
            >
              <Icon className="w-3 h-3" />
            </IconButton>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto p-2 text-xs">
        {renderTabContent(
          (tabs.some((t) => t.id === activeRightTab)
            ? activeRightTab
            : "inspector") as TabId,
        )}
      </div>
    </div>
  );
}

type EventSegment =
  | { kind: "text"; text: string }
  | { kind: "entity"; entityId: string; fallbackLabel: string };

interface GameEvent {
  id: string;
  when: string;
  category: "combat" | "movement" | "story" | "interaction";
  segments: EventSegment[];
}

interface ChatMessage {
  id: string;
  when: string;
  speaker: string;
  segments: EventSegment[];
}

type TimelineItem =
  | { kind: "event"; event: GameEvent }
  | { kind: "chat"; message: ChatMessage }
  | {
      kind: "dice-roll";
      roll: {
        id: string;
        when: string;
        speaker: string;
        expression: string;
        result: number;
        breakdown: string;
        visibility: DiceRollVisibility;
        rolls: number[];
      };
    };

interface TimelineNode {
  id: string;
  entryKind: ChatEventEntryKind;
  createdAt: number;
  speaker?: string;
  category?: GameEvent["category"];
  segments: EventSegment[];
  diceExpression?: string;
  diceResult?: number;
  diceBreakdown?: string;
  diceVisibility?: DiceRollVisibility;
  diceRolls?: number[];
}

function resolveSceneAnchorId(blocks: BlockSummary[]) {
  const location = blocks.find((b) => b.type === "location");
  if (location) return location.id;
  return DOC_ROOT_ID;
}

function parseTimelineNode(node: any): TimelineNode | null {
  if (!node || node.type !== "chat-event") return null;

  const metadata = (node.metadata ?? {}) as Record<string, unknown>;
  const rawSegments = (node.content as any)?.segments;
  const segments: EventSegment[] = Array.isArray(rawSegments)
    ? rawSegments.filter(
        (s: any) =>
          s &&
          ((s.kind === "text" && typeof s.text === "string") ||
            (s.kind === "entity" && typeof s.entityId === "string")),
      )
    : [];

  const normalizedSegments =
    segments.length > 0
      ? segments
      : [
          {
            kind: "text",
            text: typeof node.content === "string" ? node.content : "",
          } as EventSegment,
        ];

  const rawEntryKind = metadata.entryKind;
  const entryKind =
    rawEntryKind === "event"
      ? "event"
      : rawEntryKind === "dice-roll"
        ? "dice-roll"
        : "chat";

  return {
    id: node.id,
    entryKind,
    createdAt: Number(metadata.createdAt ?? Date.now()),
    speaker: typeof metadata.speaker === "string" ? metadata.speaker : "GM",
    category:
      metadata.category === "combat" ||
      metadata.category === "movement" ||
      metadata.category === "story" ||
      metadata.category === "interaction"
        ? (metadata.category as GameEvent["category"])
        : "story",
    segments: normalizedSegments,
    diceExpression:
      entryKind === "dice-roll" && typeof metadata.diceExpression === "string"
        ? metadata.diceExpression
        : undefined,
    diceResult:
      entryKind === "dice-roll" && typeof metadata.diceResult === "number"
        ? metadata.diceResult
        : undefined,
    diceBreakdown:
      entryKind === "dice-roll" && typeof metadata.diceBreakdown === "string"
        ? metadata.diceBreakdown
        : undefined,
    diceVisibility:
      entryKind === "dice-roll" &&
      (metadata.diceVisibility === "public" ||
        metadata.diceVisibility === "gm-only")
        ? metadata.diceVisibility
        : undefined,
    diceRolls:
      entryKind === "dice-roll" && Array.isArray(metadata.diceRolls)
        ? metadata.diceRolls.filter((n: unknown) => typeof n === "number")
        : undefined,
  };
}

function formatRelativeTime(ms: number) {
  const diff = Math.max(0, Date.now() - ms);
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hours = Math.floor(min / 60);
  return `${hours}h ago`;
}

function useChatTimeline(doc: any, sceneAnchorId: string): TimelineItem[] {
  const [timelineNodes, setTimelineNodes] = useState<TimelineNode[]>([]);

  useEffect(() => {
    const nodesMap = doc.getMap("nodes");
    const edgesMap = doc.getMap("edges");

    const read = () => {
      const linkedIds = new Set<string>();

      for (const [, edgeMap] of edgesMap.entries()) {
        const edge = (edgeMap as any).toJSON?.() as
          | {
              source_id?: string;
              target_id?: string;
              type?: string;
              payload?: Record<string, unknown>;
            }
          | undefined;
        if (!edge?.source_id || !edge?.target_id) continue;
        if (edge.source_id !== sceneAnchorId) continue;
        if (edge.type !== "reference" && edge.type !== "custom") continue;
        if ((edge.payload as any)?.kind !== SCENE_TIMELINE_EDGE_KIND) continue;
        linkedIds.add(edge.target_id);
      }

      const next: TimelineNode[] = [];
      linkedIds.forEach((id) => {
        const nodeMap = nodesMap.get(id) as any;
        if (!nodeMap) return;
        const parsed = parseTimelineNode(nodeMap.toJSON?.());
        if (parsed) next.push(parsed);
      });

      next.sort((a, b) => a.createdAt - b.createdAt);
      setTimelineNodes(next);
    };

    read();
    edgesMap.observeDeep(read);
    nodesMap.observeDeep(read);

    return () => {
      edgesMap.unobserveDeep(read);
      nodesMap.unobserveDeep(read);
    };
  }, [doc, sceneAnchorId]);

  return useMemo(
    () =>
      timelineNodes.map((entry) =>
        entry.entryKind === "event"
          ? {
              kind: "event" as const,
              event: {
                id: entry.id,
                when: formatRelativeTime(entry.createdAt),
                category: entry.category ?? "story",
                segments: entry.segments,
              },
            }
          : entry.entryKind === "dice-roll"
            ? {
                kind: "dice-roll" as const,
                roll: {
                  id: entry.id,
                  when: formatRelativeTime(entry.createdAt),
                  speaker: entry.speaker ?? "GM",
                  expression: entry.diceExpression ?? "",
                  result: entry.diceResult ?? 0,
                  breakdown: entry.diceBreakdown ?? "",
                  visibility: entry.diceVisibility ?? "public",
                  rolls: entry.diceRolls ?? [],
                },
              }
            : {
                kind: "chat" as const,
                message: {
                  id: entry.id,
                  when: formatRelativeTime(entry.createdAt),
                  speaker: entry.speaker ?? "GM",
                  segments: entry.segments,
                },
              },
      ),
    [timelineNodes],
  );
}

function getTypeChipClasses(colorToken?: string) {
  switch (colorToken) {
    case "emerald":
      return "border-emerald-400/30 bg-emerald-500/10 text-emerald-200";
    case "sky":
      return "border-sky-400/30 bg-sky-500/10 text-sky-200";
    case "amber":
      return "border-amber-400/30 bg-amber-500/10 text-amber-200";
    case "stone":
      return "border-stone-400/30 bg-stone-500/10 text-stone-200";
    case "violet":
      return "border-violet-400/30 bg-violet-500/10 text-violet-200";
    case "zinc":
      return "border-zinc-400/30 bg-zinc-500/10 text-zinc-200";
    default:
      return "border-white/20 bg-white/8 text-white/85";
  }
}

function ChatFeed({
  doc,
  blocks,
  typeMap,
  selectedEntityId,
  sceneAnchorId,
  onSelectEntity,
}: {
  doc: any;
  blocks: BlockSummary[];
  typeMap: ReturnType<typeof useTypeMap>;
  selectedEntityId: string | null;
  sceneAnchorId: string;
  onSelectEntity: (id: string) => void;
}) {
  const [draft, setDraft] = useState("");
  const [composeKind, setComposeKind] = useState<"chat" | "event">("chat");
  const [diceError, setDiceError] = useState("");
  const [diceVisibility, setDiceVisibility] =
    useState<DiceRollVisibility>("public");

  const timeline = useChatTimeline(doc, sceneAnchorId);
  const blockById = useMemo(
    () => new Map(blocks.map((b) => [b.id, b])),
    [blocks],
  );
  const blockNameById = useMemo(
    () => new Map(blocks.map((b) => [b.id, b.name || "Untitled"])),
    [blocks],
  );

  const submit = () => {
    const text = draft.trim();
    if (!text) return;

    const segments: EventSegment[] = [{ kind: "text", text }];

    if (selectedEntityId) {
      segments.push({ kind: "text", text: " " });
      segments.push({
        kind: "entity",
        entityId: selectedEntityId,
        fallbackLabel: blockNameById.get(selectedEntityId) ?? "Entity",
      });
    }

    const createdAt = Date.now();
    const nodeId = appendBlock(doc, {
      type: "chat-event",
      name: composeKind === "chat" ? "Chat message" : "Game event",
      content: { segments },
      metadata: {
        entryKind: composeKind,
        createdAt,
        speaker: composeKind === "chat" ? "GM" : undefined,
        category: composeKind === "event" ? "story" : undefined,
        sceneAnchorId,
      },
    });

    createEdge(doc, {
      source_id: sceneAnchorId,
      target_id: nodeId,
      type: "reference",
      payload: { kind: SCENE_TIMELINE_EDGE_KIND, entryKind: composeKind },
    });

    setDraft("");
  };

  const submitDiceRoll = (
    expression: string,
    visibility: DiceRollVisibility,
  ) => {
    try {
      const result = parseDiceExpression(expression);
      const createdAt = Date.now();
      const nodeId = appendBlock(doc, {
        type: "chat-event",
        name: "Dice roll",
        content: { segments: [] },
        metadata: {
          entryKind: "dice-roll",
          createdAt,
          speaker: "GM",
          sceneAnchorId,
          diceExpression: result.expression,
          diceResult: result.total,
          diceBreakdown: result.breakdown,
          diceVisibility: visibility,
          diceRolls: result.rolls,
        } as ChatEventMetadataDiceRoll & Record<string, unknown>,
      });

      createEdge(doc, {
        source_id: sceneAnchorId,
        target_id: nodeId,
        type: "reference",
        payload: { kind: SCENE_TIMELINE_EDGE_KIND, entryKind: "dice-roll" },
      });

      setDiceError("");
    } catch (error) {
      setDiceError(
        error instanceof Error ? error.message : "Invalid dice expression.",
      );
    }
  };

  if (timeline.length === 0) {
    return (
      <div className="flex flex-col h-full gap-2">
        <div className="text-white/30 text-[10px] mt-2">
          No chat yet. Messages, events, and dice rolls appear together here.
        </div>
        <DiceRoller
          onRoll={submitDiceRoll}
          visibility={diceVisibility}
          onVisibilityChange={setDiceVisibility}
          onError={(value) => setDiceError(value)}
        />
        {diceError ? (
          <div className="text-red-300 text-[10px]">{diceError}</div>
        ) : null}
        <ChatComposer
          draft={draft}
          setDraft={setDraft}
          composeKind={composeKind}
          setComposeKind={setComposeKind}
          onSubmit={submit}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="text-white/30 uppercase tracking-widest text-[9px] mb-2">
        Session Chat
      </div>
      <DiceRoller
        onRoll={submitDiceRoll}
        visibility={diceVisibility}
        onVisibilityChange={setDiceVisibility}
        onError={(value) => setDiceError(value)}
      />
      {diceError ? (
        <div className="text-red-300 text-[10px]">{diceError}</div>
      ) : null}
      <div className="flex-1 bg-black/30 p-1.5 rounded border border-white/5 overflow-y-auto space-y-1">
        {timeline.map((item) => {
          const id =
            item.kind === "chat"
              ? item.message.id
              : item.kind === "event"
                ? item.event.id
                : item.roll.id;
          const when =
            item.kind === "chat"
              ? item.message.when
              : item.kind === "event"
                ? item.event.when
                : item.roll.when;

          if (item.kind === "event" || item.kind === "chat") {
            const eventMeta =
              item.kind === "event"
                ? `${item.event.category} · ${when}`
                : `${item.message.speaker} · ${when}`;
            const segments =
              item.kind === "event"
                ? item.event.segments
                : item.message.segments;

            return (
              <div
                key={id}
                className="rounded border border-white/5 bg-white/[0.02] px-1.5 py-1"
              >
                <div className="text-[9px] text-white/30 uppercase tracking-wider mb-0.5">
                  {eventMeta}
                </div>
                <div className="text-[11px] text-white/80 leading-snug">
                  {segments.map((segment, index) =>
                    segment.kind === "text" ? (
                      <span key={`${id}-t-${index}`}>{segment.text}</span>
                    ) : (
                      (() => {
                        const block = blockById.get(segment.entityId);
                        const typeDef = block
                          ? typeMap.get(block.type)
                          : undefined;
                        const chipClasses = getTypeChipClasses(typeDef?.color);

                        return (
                          <button
                            key={`${id}-e-${index}`}
                            className={`inline-flex items-center h-5 px-1.5 rounded border text-[10px] font-medium leading-none align-middle transition-colors mr-0.5 ${chipClasses} hover:brightness-110`}
                            onClick={() => onSelectEntity(segment.entityId)}
                            title={`Select ${blockNameById.get(segment.entityId) ?? segment.fallbackLabel}`}
                          >
                            <span className="truncate max-w-[90px]">
                              {blockNameById.get(segment.entityId) ??
                                segment.fallbackLabel}
                            </span>
                          </button>
                        );
                      })()
                    ),
                  )}
                </div>
              </div>
            );
          }

          const diceExpression = item.roll.expression.toLowerCase();
          const hasNat20 =
            diceExpression.includes("d20") && item.roll.rolls.includes(20);
          const hasNat1 =
            diceExpression.includes("d20") &&
            item.roll.rolls.includes(1) &&
            !hasNat20;
          const rollHighlight = hasNat20
            ? "text-emerald-300"
            : hasNat1
              ? "text-red-300"
              : "text-white/80";
          const rollBorder = hasNat1
            ? "border-red-400/30 bg-red-500/5"
            : "border-amber-400/30 bg-amber-500/5";

          return (
            <div
              key={id}
              className={`rounded border ${rollBorder} px-1.5 py-1`}
              style={{ animation: "dice-roll-enter 240ms ease-out" }}
            >
              <div className="text-[9px] text-white/40 uppercase tracking-wider mb-0.5 flex items-center gap-1">
                <Dices className="w-3 h-3" />
                <span>
                  {item.roll.speaker} · {when} · {item.roll.visibility}
                </span>
              </div>
              <details className="text-[11px] text-white/80 leading-snug">
                <summary className="cursor-pointer list-none text-white/90">
                  <span className={rollHighlight}>
                    {item.roll.expression || "dice"} = {item.roll.result}
                  </span>
                  <span className="ml-2 text-[10px] text-white/50">
                    (show breakdown)
                  </span>
                </summary>
                <div className="mt-1 pl-1 text-[10px] text-white/70">
                  {item.roll.breakdown}
                </div>
              </details>
            </div>
          );
        })}
      </div>

      <ChatComposer
        draft={draft}
        setDraft={setDraft}
        composeKind={composeKind}
        setComposeKind={setComposeKind}
        onSubmit={submit}
      />
    </div>
  );
}

function ChatComposer({
  draft,
  setDraft,
  composeKind,
  setComposeKind,
  onSubmit,
}: {
  draft: string;
  setDraft: (value: string) => void;
  composeKind: "chat" | "event";
  setComposeKind: (kind: "chat" | "event") => void;
  onSubmit: () => void;
}) {
  return (
    <div className="mt-2 border border-white/5 rounded bg-black/20 p-1 flex items-center gap-px">
      <IconButton
        tooltip="Compose chat"
        className={`w-6 h-5 rounded flex items-center justify-center transition-colors ${
          composeKind === "chat"
            ? "text-white/90 bg-white/12"
            : "text-white/40 hover:text-white/70 hover:bg-white/8"
        }`}
        onClick={() => setComposeKind("chat")}
      >
        <MessageSquare className="w-3 h-3" />
      </IconButton>

      <IconButton
        tooltip="Compose event"
        className={`w-6 h-5 rounded flex items-center justify-center transition-colors ${
          composeKind === "event"
            ? "text-white/90 bg-white/12"
            : "text-white/40 hover:text-white/70 hover:bg-white/8"
        }`}
        onClick={() => setComposeKind("event")}
      >
        <ScrollText className="w-3 h-3" />
      </IconButton>

      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            onSubmit();
          }
        }}
        placeholder={composeKind === "chat" ? "Message…" : "Event summary…"}
        className="flex-1 min-w-0 h-5 px-1.5 rounded bg-white/5 border border-white/5 text-[11px] text-white/85 outline-none focus:border-white/20"
      />

      <IconButton
        tooltip="Send"
        className="w-6 h-5 rounded flex items-center justify-center text-white/60 hover:text-white/90 hover:bg-white/8 transition-colors"
        onClick={onSubmit}
      >
        <SendHorizontal className="w-3 h-3" />
      </IconButton>
    </div>
  );
}

function BlockInspector({ blockId }: { blockId: string }) {
  const doc = useYDoc();
  const block = useBlock<BlockNode & { id: string }>(blockId);
  const typeDef = useTypeDef(block?.type ?? "");
  const [selectedCondition, setSelectedCondition] = useState<
    (typeof COMMON_CONDITIONS)[number]
  >(COMMON_CONDITIONS[0]);

  if (!block) {
    return <div className="text-white/30 text-[10px]">Block not found.</div>;
  }

  const gameplayProperties =
    typeDef?.properties.filter((prop) => prop.category === "conditional") ?? [];
  const coreProperties =
    typeDef?.properties.filter((prop) => prop.category !== "conditional") ?? [];
  const maxHp = toFiniteNumber(block.properties?.maxHp) ?? 0;
  const hasGameplayProps =
    (block.type === "token" || block.type === "npc") &&
    gameplayProperties.length > 0 &&
    maxHp > 0;
  const hp = toFiniteNumber(block.properties?.hp) ?? maxHp;
  const conditions = parseConditions(block.properties?.conditions);
  const hpProp = typeDef?.properties.find((prop) => prop.key === "hp");
  const maxHpProp = typeDef?.properties.find((prop) => prop.key === "maxHp");

  const updateGameplayProperty = (changes: Record<string, unknown>) => {
    const props = { ...(block.properties ?? {}), ...changes };
    updateBlock(doc, blockId, { properties: props } as any);
  };

  const setHp = (nextHp: number) => {
    const clamped = Math.max(0, Math.min(nextHp, maxHp));
    updateGameplayProperty({ hp: clamped });
  };

  const setMaxHp = (nextMax: number) => {
    const maxValue = Math.max(0, nextMax);
    updateGameplayProperty({ hp: Math.min(hp, maxValue), maxHp: maxValue });
  };

  const toggleCondition = (condition: string) => {
    const nextConditions = setConditionsValue(conditions, condition);
    updateGameplayProperty({ conditions: nextConditions });
  };

  return (
    <div className="space-y-3">
      {/* Block header */}
      <div>
        <div className="text-white/30 uppercase tracking-widest text-[9px] mb-1">
          {typeDef?.label ?? block.type}
        </div>
        <input
          value={block.name ?? ""}
          onChange={(e) =>
            updateBlock(doc, blockId, { name: e.target.value } as any)
          }
          placeholder="Untitled"
          className="w-full bg-white/5 rounded px-1.5 py-1 text-[11px] text-white/90 outline-none border border-white/5 focus:border-white/20 transition-colors"
        />
      </div>

      {/* Separator */}
      <div className="border-b border-white/5" />

      {/* Type-defined properties */}
      {typeDef && coreProperties.length > 0 ? (
        <div className="space-y-1.5">
          <div className="text-white/30 uppercase tracking-widest text-[9px]">
            Properties
          </div>
          {coreProperties.map((prop) => (
            <PropertyField
              key={prop.key}
              def={prop}
              value={block.properties?.[prop.key] ?? prop.default}
              onChange={(val) => {
                const props = { ...(block.properties ?? {}), [prop.key]: val };
                updateBlock(doc, blockId, { properties: props } as any);
              }}
            />
          ))}
        </div>
      ) : (
        <div className="text-white/20 text-[10px] italic">
          No properties defined for this type.
        </div>
      )}

      {hasGameplayProps && (
        <div className="space-y-1.5">
          <div className="border-t border-white/5 pt-2" />
          <div className="text-white/30 uppercase tracking-widest text-[9px]">
            Gameplay
          </div>
          <div className="flex items-center px-1">
            <HpBar current={hp} max={maxHp} width={170} />
          </div>

          {hpProp && (
            <PropertyField
              def={hpProp}
              value={block.properties?.hp ?? hpProp.default}
              onChange={(val) => setHp(Number(val))}
            />
          )}

          {maxHpProp && (
            <PropertyField
              def={maxHpProp}
              value={block.properties?.maxHp ?? maxHpProp.default}
              onChange={(val) => setMaxHp(Number(val))}
            />
          )}

          <div className="flex flex-wrap items-center gap-1">
            <ConditionBadges
              conditions={conditions}
              interactive
              onToggle={toggleCondition}
            />
          </div>

          <div className="flex gap-2">
            <select
              value={selectedCondition}
              onChange={(event) => {
                const nextCondition = event.target.value;
                const typedCondition =
                  nextCondition as (typeof COMMON_CONDITIONS)[number];
                setSelectedCondition(typedCondition);
                toggleCondition(typedCondition);
              }}
              className="flex-1 h-6 min-w-0 rounded bg-white/5 border border-white/5 text-[10px] text-white/80 px-1"
            >
              {COMMON_CONDITIONS.map((condition) => (
                <option key={condition} value={condition}>
                  {condition}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => toggleCondition(selectedCondition)}
              className="h-6 px-2 rounded bg-white/10 border border-white/10 text-[10px] uppercase tracking-widest hover:bg-white/15"
            >
              Toggle
            </button>
          </div>
        </div>
      )}

      {/* Spatial info */}
      {block.spatialTransform && (
        <>
          <div className="border-b border-white/5" />
          <div className="space-y-1">
            <div className="text-white/30 uppercase tracking-widest text-[9px]">
              Position
            </div>
            <div className="text-[10px] text-white/50 font-mono">
              x: {block.spatialTransform.position.x.toFixed(1)} y:{" "}
              {block.spatialTransform.position.y.toFixed(1)} z:{" "}
              {block.spatialTransform.position.z.toFixed(1)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function PropertyField({
  def,
  value,
  onChange,
}: {
  def: PropertyDef;
  value: unknown;
  onChange: (val: unknown) => void;
}) {
  const baseInput =
    "w-full bg-white/5 rounded px-1.5 py-[3px] text-[11px] text-white/80 outline-none border border-white/5 focus:border-white/20 transition-colors";

  return (
    <div className="flex items-center gap-2">
      <span className="text-[10px] text-white/40 w-14 shrink-0 truncate">
        {def.label}
      </span>

      {def.type === "string" && (
        <input
          className={baseInput}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}

      {def.type === "number" && (
        <input
          className={baseInput}
          type="number"
          value={(value as number) ?? 0}
          onChange={(e) => onChange(Number(e.target.value))}
        />
      )}

      {def.type === "boolean" && (
        <button
          className={`w-6 h-4 rounded-full transition-colors ${
            value ? "bg-emerald-500/60" : "bg-white/10"
          }`}
          onClick={() => onChange(!value)}
        >
          <div
            className={`w-3 h-3 rounded-full bg-white transition-transform ${
              value ? "translate-x-2.5" : "translate-x-0.5"
            }`}
          />
        </button>
      )}

      {def.type === "select" && (
        <select
          className={`${baseInput} appearance-auto`}
          value={(value as string) ?? ""}
          onChange={(e) => onChange(e.target.value)}
        >
          {(def.options ?? []).map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      )}

      {def.type === "color" && (
        <div className="flex items-center gap-1 flex-1">
          <input
            type="color"
            value={(value as string) ?? "#ffffff"}
            onChange={(e) => onChange(e.target.value)}
            className="w-5 h-5 rounded border-0 cursor-pointer bg-transparent"
          />
          <span className="text-[10px] text-white/40 font-mono">
            {(value as string) ?? "#fff"}
          </span>
        </div>
      )}
    </div>
  );
}
